import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  DeviceFieldValues,
  getDeviceFieldsFromModels,
} from "@services/catalog";
import { DeviceModelDetail } from "@services/catalog/types";
import { useErrorNotification } from "@common/components/Notification";
import { CatalogContext } from "@common/components/CatalogProvider";

export type DeviceFieldContextType = DeviceFieldValues & { models: string[] };

const defaultValues: DeviceFieldContextType = {
  manufacturers: [],
  deviceTypes: [],
  categories: [],
  models: [],
};
export const DeviceFieldValueContext =
  createContext<DeviceFieldContextType>(defaultValues);

const applyFilterToFieldValues = (
  models: DeviceModelDetail[],
  values: DeviceFieldContextType,
  filter?: Partial<WidgetFilter>
): DeviceFieldContextType => {
  if (
    filter === undefined ||
    filter.filterType !== "target" ||
    filter.payload === undefined
  )
    return values;
  const { manufacturer, deviceType, category, model } =
    filter.payload as TargetFilterPayload;

  const tests: Array<(model: DeviceModelDetail) => boolean> = [];
  if (model) tests.push((m) => m.model === model);
  if (manufacturer) tests.push((m) => m.manufacturer === manufacturer);
  if (category) tests.push((m) => m.category === category);
  if (deviceType) tests.push((m) => m.deviceType === deviceType);
  const modelNames: { [k in string]: any } = {};

  const filtered = models.filter((m) => {
    let results = tests.every((fn) => fn(m));
    if (results) modelNames[m.model as string] = true;
    return results;
  });

  return {
    ...getDeviceFieldsFromModels(filtered),
    models: Object.keys(modelNames),
  };
};

const sortFieldValues = (fieldValues: DeviceFieldContextType) => {
  const sortFn = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: "base" });

  fieldValues.manufacturers.sort(sortFn);
  fieldValues.categories.sort(sortFn);
  fieldValues.models.sort(sortFn);
  fieldValues.deviceTypes.sort(sortFn);
  return fieldValues;
};
export const DeviceFieldValueProvider = ({
  targetFilter,
  children,
}: PropsWithChildren<{ targetFilter?: Partial<WidgetFilter> }>) => {
  const [fieldValues, setFieldValues] = useState<DeviceFieldContextType>();
  const globalCatalog =
    useContext(CatalogContext)?.deviceModelDetails?.list ?? [];
  const [models, setModels] = useState<DeviceModelDetail[]>();
  const dispatchError = useErrorNotification();
  const refetch = async () => {
    try {
      const models = globalCatalog;
      const modelNames: { [k in string]: any } = {};
      models.forEach((model) => (modelNames[model.model as string] = true));
      setFieldValues({
        ...getDeviceFieldsFromModels(models),
        models: Object.keys(modelNames),
      });
      setModels(models);
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return (
    <DeviceFieldValueContext.Provider
      value={sortFieldValues(
        applyFilterToFieldValues(
          models ?? [],
          { ...defaultValues, ...fieldValues },
          targetFilter
        )
      )}
    >
      {children}
    </DeviceFieldValueContext.Provider>
  );
};

export default DeviceFieldValueContext;
